import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, Package, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const PackagesPage = () => {
  const { user, selectedBuilding } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [formData, setFormData] = useState({
    building_id: '',
    recipient_unit: '',
    recipient_name: '',
    description: '',
    carrier: ''
  });
  const [deliveryData, setDeliveryData] = useState({
    delivered_to: ''
  });

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      const defaultBuilding = (selectedBuilding && selectedBuilding !== 'all') 
        ? selectedBuilding 
        : (response.data.length > 0 ? response.data[0].id : '');
      setFormData(prev => ({ ...prev, building_id: defaultBuilding }));
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const response = await axios.get(`${API}/packages${query}`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/packages`, formData);
      toast.success('Paquete registrado exitosamente');
      setDialogOpen(false);
      setFormData({
        building_id: buildings[0]?.id || '',
        recipient_unit: '',
        recipient_name: '',
        description: '',
        carrier: ''
      });
      fetchPackages();
    } catch (error) {
      toast.error('Error al registrar paquete');
    }
  };

  const handleDelivery = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/packages/${selectedPackageId}/deliver`, deliveryData);
      toast.success('Paquete entregado');
      setDeliveryDialogOpen(false);
      setDeliveryData({ delivered_to: '' });
      fetchPackages();
    } catch (error) {
      toast.error('Error al registrar entrega');
    }
  };

  const openDeliveryDialog = (pkgId) => {
    setSelectedPackageId(pkgId);
    setDeliveryDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingPackages = packages.filter(p => p.status === 'pending');
  const deliveredPackages = packages.filter(p => p.status === 'delivered');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">Paquetería</h1>
            <p className="text-text-secondary mt-1">Recepción y entrega de correspondencia</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'staff') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent/90 text-white rounded-full">
                  <Plus className="h-5 w-5 mr-2" />
                  Registrar Paquete
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Registrar recepción de paquete</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Conjunto</Label>
                    <Select value={formData.building_id} onValueChange={(value) => setFormData({...formData, building_id: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(building => (
                          <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Unidad / Apto</Label>
                      <Input
                        value={formData.recipient_unit}
                        onChange={(e) => setFormData({...formData, recipient_unit: e.target.value})}
                        required
                        placeholder="Ej: 301"
                      />
                    </div>
                    <div>
                      <Label>Destinatario (Opcional)</Label>
                      <Input
                        value={formData.recipient_name}
                        onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                        placeholder="Nombre residente"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      placeholder="Ej: Caja de Amazon, Sobre..."
                    />
                  </div>
                  <div>
                    <Label>Transportadora / Mensajero</Label>
                    <Input
                      value={formData.carrier}
                      onChange={(e) => setFormData({...formData, carrier: e.target.value})}
                      placeholder="Ej: Servientrega, Rappi..."
                    />
                  </div>
                  <Button type="submit" className="w-full bg-accent hover:bg-accent/90">
                    Registrar Recepción
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
            <TabsTrigger value="pending">Pendientes ({pendingPackages.length})</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pendingPackages.length === 0 ? (
                <div className="col-span-full text-center py-12 bg-white rounded-xl border border-slate-100">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay paquetes pendientes de entrega</p>
                </div>
              ) : (
                pendingPackages.map((pkg) => (
                  <Card key={pkg.id} className="p-5 hover:shadow-md transition-shadow border-l-4 border-l-orange-500">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-primary">{pkg.recipient_unit}</h3>
                        {pkg.recipient_name && <p className="text-sm text-text-secondary">{pkg.recipient_name}</p>}
                      </div>
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        Pendiente
                      </Badge>
                    </div>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Package className="h-4 w-4" />
                        <span>{pkg.description}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Clock className="h-4 w-4" />
                        <span>{new Date(pkg.received_at).toLocaleString('es-ES')}</span>
                      </div>
                      {pkg.carrier && (
                        <p className="text-xs text-slate-400">Por: {pkg.carrier}</p>
                      )}
                    </div>
                    {(user?.role === 'admin' || user?.role === 'staff') && (
                      <Button 
                        className="w-full bg-primary/10 text-primary hover:bg-primary hover:text-white"
                        onClick={() => openDeliveryDialog(pkg.id)}
                      >
                        Entregar
                      </Button>
                    )}
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="history" className="mt-6">
             <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
               {deliveredPackages.length === 0 ? (
                 <div className="text-center py-12">
                   <p className="text-slate-500">No hay historial de entregas</p>
                 </div>
               ) : (
                 <div className="divide-y divide-slate-100">
                   {deliveredPackages.map((pkg) => (
                     <div key={pkg.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                       <div className="flex items-center gap-4">
                         <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                           <CheckCircle className="h-5 w-5" />
                         </div>
                         <div>
                           <p className="font-semibold text-primary">{pkg.recipient_unit} - {pkg.description}</p>
                           <p className="text-xs text-slate-500">
                             Entregado a: {pkg.delivered_to} • {new Date(pkg.delivered_at).toLocaleDateString('es-ES')}
                           </p>
                         </div>
                       </div>
                       <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">Entregado</Badge>
                     </div>
                   ))}
                 </div>
               )}
             </div>
          </TabsContent>
        </Tabs>

        {/* Delivery Dialog */}
        <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Entrega</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleDelivery} className="space-y-4">
              <div>
                <Label>¿Quién recibe el paquete?</Label>
                <Input
                  value={deliveryData.delivered_to}
                  onChange={(e) => setDeliveryData({ delivered_to: e.target.value })}
                  required
                  placeholder="Nombre de quien recibe"
                />
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                Confirmar Entrega
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PackagesPage;
